/**
 * Prompt Service
 * Handles prompt management operations (get, set, clear)
 */

import { CHATBOT_API_BASE_URL } from '../utils/apiConfig';

export const promptService = {
    /**
     * Get current prompt for a session
     * @param {string|null} sessionId - Session identifier
     * @param {boolean} answerConfirmed - Whether answer is confirmed
     * @returns {Promise<Object>} Prompt data
     */
    async getCurrentPrompt(sessionId = null, answerConfirmed = false) {
        try {
            const params = new URLSearchParams({
                answer_confirmed: String(Boolean(answerConfirmed))
            });
            if (sessionId) {
                params.append('session_id', sessionId);
            }

            const response = await fetch(
                `${CHATBOT_API_BASE_URL}/api/prompts/current?${params}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to get prompt: ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            console.error("Error getting current prompt:", error);
            throw error;
        }
    },

    /**
     * Get default prompt (non-customized)
     * @param {boolean} answerConfirmed - Whether answer is confirmed
     * @returns {Promise<Object>} Default prompt data
     */
    async getDefaultPrompt(answerConfirmed = false) {
        try {
            const params = new URLSearchParams({
                answer_confirmed: String(Boolean(answerConfirmed))
            });

            const response = await fetch(
                `${CHATBOT_API_BASE_URL}/api/prompts/default?${params}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to get default prompt: ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            console.error("Error getting default prompt:", error);
            throw error;
        }
    },

    /**
     * Set custom prompt for a session
     * @param {string} sessionId - Session identifier
     * @param {string} customPrompt - Custom prompt text
     * @param {boolean} answerConfirmed - Whether answer is confirmed
     * @returns {Promise<Object>} Response data
     */
    async setSessionPrompt(sessionId, customPrompt, answerConfirmed = false) {
        try {
            const response = await fetch(
                `${CHATBOT_API_BASE_URL}/api/prompts/set`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        session_id: sessionId,
                        custom_prompt: customPrompt,
                        answer_confirmed: answerConfirmed
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to set prompt: ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            console.error("Error setting session prompt:", error);
            throw error;
        }
    },

    /**
     * Clear custom prompt for a session (revert to default)
     * @param {string} sessionId - Session identifier
     * @returns {Promise<Object>} Response data
     */
    async clearSessionPrompt(sessionId) {
        try {
            const params = new URLSearchParams({
                session_id: sessionId
            });

            const response = await fetch(
                `${CHATBOT_API_BASE_URL}/api/prompts/clear?${params}`,
                {
                    method: "DELETE",
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to clear prompt: ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            console.error("Error clearing session prompt:", error);
            throw error;
        }
    },
};
